/**
 * Customer Manager Store
 *
 * Zustand store for customer list and detail state management.
 * Handles data fetching, URL synchronization, selection, and inline editing.
 */

import { create } from 'zustand'
import {
  listCustomers,
  searchCustomers,
  fetchCustomerDetail,
  updateCustomer,
  updateNarrative,
  updateOpportunity,
  getCustomerEvents,
  getCustomerPurchases,
  fetchCustomersForExport,
  regenerateProfile,
  generateInsight,
  grantEntitlement,
  revokeEntitlementGrant,
  type RegenerateProfileResponse,
  type GenerateInsightResponse,
} from './api'
import {
  effectiveLastSeenAt,
  effectiveStatus,
  extractDomain,
} from './display'
import type {
  Customer,
  CustomerFilters,
  CustomerSortState,
  CustomerDetail,
  CustomerInspectorTab,
  CustomerNarrative,
  CustomerOpportunity,
  CustomerStatus,
  CreateEntitlementGrantRequest,
  EntitlementGrant,
} from './types'

// ── Store State ─────────────────────────────────────────────────────────────

interface CustomersStoreState {
  // List state
  /**
   * The unfiltered, unsorted base dataset as fetched from the server. All
   * client-side filtering and sorting is derived from this buffer so that
   * applying or removing a filter never destroys the underlying data.
   */
  customersAll: Customer[]
  /**
   * The displayed list — always equal to `applyClientSort(applyClientFilters(customersAll, filters), sortState)`.
   */
  customers: Customer[]
  cursor: string | null
  total: number
  hasMore: boolean
  filters: CustomerFilters
  sortState: CustomerSortState

  // Selection state
  selectedIds: Set<string>
  selectedId: string | null

  // Detail state
  detailCache: Map<string, CustomerDetail>
  activeTab: CustomerInspectorTab

  // Loading states
  listLoading: boolean
  listError: string | null
  loadingMore: boolean
  detailLoading: boolean
  detailError: string | null

  // Agent-driven regeneration state — tracked per customer ID so an
  // operator can navigate away mid-run and return to the same in-flight
  // operation. The Set holds customer IDs with an in-flight request.
  regeneratingProfile: Set<string>
  generatingInsight: Set<string>

  // Last updated
  lastFetchedAt: number | null
}

// ── Store Actions ───────────────────────────────────────────────────────────

interface CustomersStoreActions {
  // List operations
  loadList(): Promise<void>
  loadMore(): Promise<void>
  setFilters(filters: CustomerFilters): void
  setSort(sort: CustomerSortState): void
  clearFilters(): void

  // Selection
  selectCustomer(id: string | null): void
  toggleSelection(id: string): void
  selectAll(): void
  clearSelection(): void

  // Detail operations
  setActiveTab(tab: CustomerInspectorTab): void
  loadCustomerDetail(id: string): Promise<void>
  loadCustomerEvents(id: string): Promise<void>
  loadCustomerPurchases(id: string): Promise<void>

  // Inline editing — only fields the backend PATCH /v1/customers/{id}
  // endpoint actually accepts (name, status). Email is intentionally
  // excluded; tier was removed (it never existed on the backend).
  inlineUpdate(
    id: string,
    patch: Partial<Pick<Customer, 'name' | 'status'>>
  ): Promise<void>
  saveNarrative(id: string, narrative: CustomerNarrative): Promise<void>
  saveOpportunity(id: string, opportunity: CustomerOpportunity): Promise<void>

  // Entitlement grants — grant or revoke an entitlement on a customer.
  // Both invalidate the customer's detail cache and trigger a re-fetch so
  // the Overview tab's Entitlements/Features sections reflect the change
  // without the operator having to navigate away.
  grantCustomerEntitlement(
    customerId: string,
    body: Omit<CreateEntitlementGrantRequest, 'customer_id'>,
  ): Promise<EntitlementGrant>
  revokeCustomerEntitlementGrant(
    customerId: string,
    grantId: string,
  ): Promise<EntitlementGrant>

  // Agent-driven regeneration
  /**
   * Trigger fresh narrative + opportunity synthesis for the given customer.
   * Marks the customer as in-flight, awaits the agent (~30-90s), and on
   * success invalidates the detail cache so the next read pulls the fresh
   * narrative/opportunity. Resolves with the agent's response so callers
   * can show success/error toasts.
   */
  regenerateCustomerProfile(id: string): Promise<RegenerateProfileResponse>
  /**
   * Trigger fresh insight generation for the given customer. The caller
   * supplies the app context (`app_id`) — the page derives this from the
   * customer's origin and falls back to a sensible default.
   */
  generateCustomerInsight(
    id: string,
    appId: string,
  ): Promise<GenerateInsightResponse>

  // Bulk operations
  bulkExport(): Promise<Record<string, unknown>[]>

  // URL sync
  hydrateFromUrl(searchParams: URLSearchParams): void
  serializeToUrl(): URLSearchParams
}

type CustomersStore = CustomersStoreState & CustomersStoreActions

// ── Initial State ───────────────────────────────────────────────────────────

const initialState: CustomersStoreState = {
  customersAll: [],
  customers: [],
  cursor: null,
  total: 0,
  hasMore: false,
  filters: {},
  sortState: { column: 'last_seen_at', direction: 'desc' },
  selectedIds: new Set(),
  selectedId: null,
  detailCache: new Map(),
  activeTab: 'overview',
  listLoading: false,
  listError: null,
  loadingMore: false,
  detailLoading: false,
  detailError: null,
  regeneratingProfile: new Set(),
  generatingInsight: new Set(),
  lastFetchedAt: null,
}

// ── Helper: Apply client-side filters ───────────────────────────────────────

function applyClientFilters(
  customers: Customer[],
  filters: CustomerFilters
): Customer[] {
  let result = customers

  // Text search (name, email, id)
  if (filters.q) {
    const q = filters.q.toLowerCase()
    result = result.filter(
      (c) =>
        c.email.toLowerCase().includes(q) ||
        (c.name?.toLowerCase().includes(q) ?? false) ||
        c.id.toLowerCase().includes(q)
    )
  }

  // Status filter — uses canonical backend values plus the synthetic
  // 'unknown' bucket for customers whose `status` is empty / missing.
  if (filters.status && filters.status.length > 0) {
    const wanted = new Set(filters.status)
    result = result.filter((c) => wanted.has(effectiveStatus(c)))
  }

  // Domain filter (client-side derived)
  if (filters.domain && filters.domain.length > 0) {
    result = result.filter((c) => {
      const domain = extractDomain(c.email)
      return filters.domain!.includes(domain)
    })
  }

  // Tag filter — matches customers whose `tags` array contains ALL of the
  // requested tags. AND semantics rather than OR so chips can be composed
  // (e.g. "new users that are also at_risk") without surprising the operator
  // with a sudden widening of the result set.
  if (filters.tags && filters.tags.length > 0) {
    const wanted = filters.tags
    result = result.filter((c) => {
      const tags = c.tags
      if (!tags || tags.length === 0) return false
      return wanted.every((t) => tags.includes(t))
    })
  }

  // Email verification filter — strict boolean match. `undefined` means
  // "no filter"; `true` / `false` map to "verified" / "not verified". A
  // missing `email_verified` field on the customer is treated as `false`
  // because the identity service defaults the flag to `false` on signup.
  if (filters.email_verified !== undefined) {
    const wanted = filters.email_verified
    result = result.filter((c) => Boolean(c.email_verified) === wanted)
  }

  // Date filters
  if (filters.joined_from) {
    const fromTs = new Date(filters.joined_from).getTime() / 1000
    result = result.filter((c) => c.created >= fromTs)
  }
  if (filters.joined_to) {
    const toTs = new Date(filters.joined_to).getTime() / 1000
    result = result.filter((c) => c.created <= toTs)
  }
  if (filters.active_from) {
    const fromTs = new Date(filters.active_from).getTime() / 1000
    result = result.filter((c) => {
      const last = effectiveLastSeenAt(c)
      return last !== undefined && last >= fromTs
    })
  }
  if (filters.active_to) {
    const toTs = new Date(filters.active_to).getTime() / 1000
    result = result.filter((c) => {
      const last = effectiveLastSeenAt(c)
      return last === undefined || last <= toTs
    })
  }

  return result
}

// ── Helper: Apply client-side sorting ───────────────────────────────────────

function applyClientSort(
  customers: Customer[],
  sort: CustomerSortState
): Customer[] {
  if (!sort.column || !sort.direction) return customers

  const sorted = [...customers].sort((a, b) => {
    let aVal: string | number = 0
    let bVal: string | number = 0

    switch (sort.column) {
      case 'name':
        aVal = (a.name ?? '').toLowerCase()
        bVal = (b.name ?? '').toLowerCase()
        break
      case 'email':
        aVal = a.email.toLowerCase()
        bVal = b.email.toLowerCase()
        break
      case 'created':
        aVal = a.created
        bVal = b.created
        break
      case 'updated':
        aVal = a.updated
        bVal = b.updated
        break
      case 'last_seen_at':
        aVal = effectiveLastSeenAt(a) ?? 0
        bVal = effectiveLastSeenAt(b) ?? 0
        break
      case 'status':
        aVal = effectiveStatus(a)
        bVal = effectiveStatus(b)
        break
      default:
        return 0
    }

    if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1
    return 0
  })

  return sorted
}

// ── Helper: Merge a new page into the accumulator ──────────────────────────
//
// The backend's cursor-based pagination can return the same document on
// adjacent pages (see customer_repository.go caveat: Firestore paginates by
// document-ID order but the cursor is derived from the in-memory Created
// sort). This helper skips records from a new page whose ID already appeared
// in a prior page — those are pagination artifacts.
//
// IMPORTANT: Duplicates *within* a single backend response are NEVER removed.
// If the backend itself returns two records with the same ID on one page,
// that indicates a real data integrity issue and the operator needs to see
// every record.

function mergePageIntoAccumulator(
  existing: Customer[],
  newPage: Customer[],
): Customer[] {
  const seen = new Set(existing.map((c) => c.id))
  const toAdd = newPage.filter((c) => !seen.has(c.id))
  return [...existing, ...toAdd]
}

// ── Store Implementation ────────────────────────────────────────────────────

export const useCustomersStore = create<CustomersStore>((set, get) => ({
  ...initialState,

  loadList: async () => {
    set({ listLoading: true, listError: null })

    try {
      const state = get()

      // When a search query is present, use the /v1/customers/search endpoint
      // which runs indexed Firestore array-contains lookups against the
      // denormalised external_identifier_ids array. This is a single O(log n)
      // query per candidate key rather than a full-table scan, and it finds
      // customers by any identifier (email, Stripe, RevenueCat, Apple, Google,
      // Firebase, customer ID) including additional_emails aliases.
      const hasSearchQuery = !!state.filters.q

      let allCustomers: Customer[] = []

      if (hasSearchQuery) {
        const response = await searchCustomers(state.filters.q!)
        allCustomers = response.data ?? []
      } else {
        // Normal pagination - first page only
        const response = await listCustomers({ limit: 100 })
        allCustomers = response.data ?? []

        // Set cursor for pagination
        const nextCursor = response.next_page_token && response.next_page_token.length > 0
          ? response.next_page_token
          : null

        set({
          cursor: nextCursor,
          hasMore: response.has_more ?? false,
        })
      }

      // Apply client-side filtering and sorting from the base dataset
      const filtered = applyClientFilters(allCustomers, state.filters)
      const sorted = applyClientSort(filtered, state.sortState)

      set({
        customersAll: allCustomers,
        customers: sorted,
        total: sorted.length,
        listLoading: false,
        lastFetchedAt: Date.now(),
      })

      // When searching, disable pagination
      if (hasSearchQuery) {
        set({ cursor: null, hasMore: false })
      }
    } catch (error) {
      set({
        listLoading: false,
        listError:
          error instanceof Error ? error.message : 'Failed to load customers',
      })
    }
  },

  loadMore: async () => {
    const state = get()
    if (state.loadingMore || !state.hasMore || !state.cursor) return

    set({ loadingMore: true })

    try {
      const response = await listCustomers({
        limit: 100,
        cursor: state.cursor,
      })

      // Merge new page into the base dataset, skipping records already
      // fetched on a prior page (pagination cursor artifact). Duplicates
      // within a single response are always preserved.
      const newAll = mergePageIntoAccumulator(
        state.customersAll ?? [],
        response.data ?? [],
      )
      const filtered = applyClientFilters(newAll, state.filters)
      const sorted = applyClientSort(filtered, state.sortState)

      // Cursor is the server-issued next_page_token (task 2808).
      const nextCursor = response.next_page_token && response.next_page_token.length > 0
        ? response.next_page_token
        : null

      set({
        customersAll: newAll,
        customers: sorted,
        cursor: nextCursor,
        total: newAll.length,
        hasMore: response.has_more,
        loadingMore: false,
      })
    } catch {
      set({ loadingMore: false })
    }
  },

  setFilters: (filters) => {
    const prevFilters = get().filters
    const wasSearching = !!prevFilters.q
    const isSearching = !!filters.q

    set({ filters })

    // When the search query changes (added, removed, or modified), fetch
    // fresh data so we search across the complete database, not just the
    // currently-loaded pages.
    if (isSearching || wasSearching) {
      get().loadList()
      return
    }

    // For non-search filters (status, domain, dates), re-derive the
    // displayed list from the stable `customersAll` base dataset. This
    // way clearing or relaxing a filter restores the previous rows
    // without a network round-trip.
    const state = get()
    const filtered = applyClientFilters(state.customersAll, filters)
    const sorted = applyClientSort(filtered, state.sortState)
    set({ customers: sorted })
  },

  setSort: (sortState) => {
    set({ sortState })
    // Re-apply sort against the currently displayed dataset (which is
    // the filtered slice of `customersAll`). Sorting is idempotent over
    // any subset, so we don't need to reach back to the base.
    const state = get()
    const sorted = applyClientSort(state.customers, sortState)
    set({ customers: sorted })
  },

  clearFilters: () => {
    set({ filters: {} })
    // No network needed — re-derive from the base dataset.
    const state = get()
    const sorted = applyClientSort(state.customersAll, state.sortState)
    set({ customers: sorted })
  },

  selectCustomer: (id) => {
    set({ selectedId: id, activeTab: 'overview' })
    if (id) {
      get().loadCustomerDetail(id)
    }
  },

  toggleSelection: (id) => {
    set((state) => {
      const newSelected = new Set(state.selectedIds)
      if (newSelected.has(id)) {
        newSelected.delete(id)
      } else {
        newSelected.add(id)
      }
      return { selectedIds: newSelected }
    })
  },

  selectAll: () => {
    const state = get()
    const allIds = new Set(state.customers.map((c) => c.id))
    set({ selectedIds: allIds })
  },

  clearSelection: () => {
    set({ selectedIds: new Set() })
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab })

    // Load tab-specific data if needed
    const state = get()
    if (state.selectedId) {
      if (tab === 'timeline') {
        get().loadCustomerEvents(state.selectedId)
      } else if (tab === 'purchases') {
        get().loadCustomerPurchases(state.selectedId)
      }
    }
  },

  loadCustomerDetail: async (id) => {
    // Check cache first
    const cached = get().detailCache.get(id)
    if (cached && Date.now() - cached.lastFetchedAt < 30000) {
      // Cache valid for 30 seconds
      return
    }

    set({ detailLoading: true, detailError: null })

    try {
      const detail = await fetchCustomerDetail(id)

      if (detail.customer) {
        set((state) => {
          const newCache = new Map(state.detailCache)
          newCache.set(id, {
            customer: detail.customer!,
            narrative: detail.narrative,
            opportunity: detail.opportunity,
            lastFetchedAt: Date.now(),
          })
          return {
            detailCache: newCache,
            detailLoading: false,
          }
        })
      } else {
        set({
          detailLoading: false,
          detailError: detail.errors.customer ?? 'Customer not found',
        })
      }
    } catch (error) {
      set({
        detailLoading: false,
        detailError:
          error instanceof Error
            ? error.message
            : 'Failed to load customer details',
      })
    }
  },

  loadCustomerEvents: async (id) => {
    // Always write *something* (success data or empty array) so the
    // Timeline tab cannot get stuck in a perpetual loading skeleton on
    // a network failure or 404.
    let events: CustomerDetail['events'] = []
    try {
      const response = await getCustomerEvents(id, { limit: 50 })
      events = response.data ?? []
    } catch (error) {
      console.error('Failed to load customer events:', error)
      events = []
    }

    set((state) => {
      const existing = state.detailCache.get(id)
      if (!existing) return state

      const newCache = new Map(state.detailCache)
      newCache.set(id, {
        ...existing,
        events,
      })
      return { detailCache: newCache }
    })
  },

  loadCustomerPurchases: async (id) => {
    // Always write *something* (success data or empty array) so the
    // Purchases tab cannot get stuck in a perpetual loading skeleton.
    let purchases: CustomerDetail['purchases'] = []
    try {
      const response = await getCustomerPurchases(id)
      purchases = response.data ?? []
    } catch (error) {
      console.error('Failed to load customer purchases:', error)
      purchases = []
    }

    set((state) => {
      const existing = state.detailCache.get(id)
      if (!existing) return state

      const newCache = new Map(state.detailCache)
      newCache.set(id, {
        ...existing,
        purchases,
      })
      return { detailCache: newCache }
    })
  },

  inlineUpdate: async (id, patch) => {
    // Optimistic update — patch both `customersAll` (so the row sticks
    // around when filters change) and `customers` (the displayed list).
    set((state) => {
      const updateRow = (rows: Customer[]): Customer[] => {
        const idx = rows.findIndex((c) => c.id === id)
        if (idx === -1) return rows
        const next = [...rows]
        next[idx] = { ...rows[idx], ...patch }
        return next
      }

      const newAll = updateRow(state.customersAll)
      const newCustomers = updateRow(state.customers)

      const existing = state.detailCache.get(id)
      const newCache = new Map(state.detailCache)
      if (existing) {
        newCache.set(id, {
          ...existing,
          customer: { ...existing.customer, ...patch },
        })
      }

      return {
        customersAll: newAll,
        customers: newCustomers,
        detailCache: newCache,
      }
    })

    try {
      // The backend PATCH endpoint only accepts a strict subset of
      // fields. Filter the patch defensively so an accidental extra
      // key never ships to the server.
      const apiPatch: { name?: string; status?: Exclude<CustomerStatus, ''> } = {}
      if (patch.name !== undefined) apiPatch.name = patch.name
      if (patch.status !== undefined && patch.status !== '') {
        apiPatch.status = patch.status
      }

      const updated = await updateCustomer(id, apiPatch)

      // Update with server response — reconcile both `customersAll` and
      // `customers` to the canonical record.
      set((state) => {
        const replaceRow = (rows: Customer[]): Customer[] => {
          const idx = rows.findIndex((c) => c.id === id)
          if (idx === -1) return rows
          const next = [...rows]
          next[idx] = updated
          return next
        }

        const existing = state.detailCache.get(id)
        const newCache = new Map(state.detailCache)
        if (existing) {
          newCache.set(id, {
            ...existing,
            customer: updated,
          })
        }

        return {
          customersAll: replaceRow(state.customersAll),
          customers: replaceRow(state.customers),
          detailCache: newCache,
        }
      })
    } catch (error) {
      // Revert on error - reload list
      get().loadList()
      throw error
    }
  },

  saveNarrative: async (id, narrative) => {
    const updated = await updateNarrative(id, narrative)

    set((state) => {
      const existing = state.detailCache.get(id)
      if (!existing) return state

      const newCache = new Map(state.detailCache)
      newCache.set(id, {
        ...existing,
        narrative: updated,
      })
      return { detailCache: newCache }
    })
  },

  saveOpportunity: async (id, opportunity) => {
    const updated = await updateOpportunity(id, opportunity)

    set((state) => {
      const existing = state.detailCache.get(id)
      if (!existing) return state

      const newCache = new Map(state.detailCache)
      newCache.set(id, {
        ...existing,
        opportunity: updated,
      })
      return { detailCache: newCache }
    })
  },

  grantCustomerEntitlement: async (customerId, body) => {
    const response = await grantEntitlement({
      ...body,
      customer_id: customerId,
    })

    // Invalidate the cache so the Overview tab's Entitlements/Features
    // sections reflect the new grant on the next render. Then re-fetch
    // immediately so the operator sees the change without navigating away.
    set((state) => {
      const existing = state.detailCache.get(customerId)
      if (!existing) return state
      const newCache = new Map(state.detailCache)
      newCache.set(customerId, { ...existing, lastFetchedAt: 0 })
      return { detailCache: newCache }
    })
    await get().loadCustomerDetail(customerId)

    return response
  },

  revokeCustomerEntitlementGrant: async (customerId, grantId) => {
    const response = await revokeEntitlementGrant(grantId)

    // Same cache-bust pattern as grantCustomerEntitlement above — the
    // expanded entitlements/features lists on the customer record need to
    // be recomputed by the backend.
    set((state) => {
      const existing = state.detailCache.get(customerId)
      if (!existing) return state
      const newCache = new Map(state.detailCache)
      newCache.set(customerId, { ...existing, lastFetchedAt: 0 })
      return { detailCache: newCache }
    })
    await get().loadCustomerDetail(customerId)

    return response
  },

  regenerateCustomerProfile: async (id) => {
    // Mark this customer as in-flight so the UI can disable the button and
    // show a spinner. Use functional updates to keep the Set immutable.
    set((state) => {
      const next = new Set(state.regeneratingProfile)
      next.add(id)
      return { regeneratingProfile: next }
    })

    try {
      const response = await regenerateProfile(id)

      // Whether the agent succeeded or declined, drop the cached detail so
      // the next read pulls fresh narrative + opportunity. We don't trust
      // partial in-place updates because the agent rewrites both records.
      set((state) => {
        const existing = state.detailCache.get(id)
        if (!existing) return state
        const newCache = new Map(state.detailCache)
        newCache.set(id, { ...existing, lastFetchedAt: 0 })
        return { detailCache: newCache }
      })

      // Re-fetch so the UI updates without the user navigating away. This
      // runs after we've cleared the cache so loadCustomerDetail will
      // actually hit the network.
      if (response.success) {
        await get().loadCustomerDetail(id)
      }

      return response
    } finally {
      set((state) => {
        const next = new Set(state.regeneratingProfile)
        next.delete(id)
        return { regeneratingProfile: next }
      })
    }
  },

  generateCustomerInsight: async (id, appId) => {
    set((state) => {
      const next = new Set(state.generatingInsight)
      next.add(id)
      return { generatingInsight: next }
    })

    try {
      // Insights are persisted by the agent itself; we don't need to mutate
      // the customer detail cache here. The Overview tab doesn't render the
      // insight history yet, so a refresh is unnecessary at this point.
      return await generateInsight(id, { app_id: appId })
    } finally {
      set((state) => {
        const next = new Set(state.generatingInsight)
        next.delete(id)
        return { generatingInsight: next }
      })
    }
  },

  bulkExport: async () => {
    const state = get()
    return fetchCustomersForExport(state.filters) as unknown as Record<string, unknown>[]
  },

  hydrateFromUrl: (searchParams) => {
    const filters: CustomerFilters = {}

    const q = searchParams.get('q')
    if (q) filters.q = q

    const status = searchParams.get('status')
    if (status) {
      filters.status = status
        .split(',')
        .map((s) => s.trim())
        .filter(
          (s): s is Exclude<CustomerStatus, ''> | 'unknown' =>
            s === 'lead' ||
            s === 'registered' ||
            s === 'active' ||
            s === 'churned' ||
            s === 'unknown'
        )
    }

    const domain = searchParams.get('domain')
    if (domain) filters.domain = domain.split(',')

    const tags = searchParams.get('tags')
    if (tags) {
      filters.tags = tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
    }

    const emailVerified = searchParams.get('email_verified')
    if (emailVerified === 'true') filters.email_verified = true
    else if (emailVerified === 'false') filters.email_verified = false

    const joinedFrom = searchParams.get('joined_from')
    if (joinedFrom) filters.joined_from = joinedFrom

    const joinedTo = searchParams.get('joined_to')
    if (joinedTo) filters.joined_to = joinedTo

    const activeFrom = searchParams.get('active_from')
    if (activeFrom) filters.active_from = activeFrom

    const activeTo = searchParams.get('active_to')
    if (activeTo) filters.active_to = activeTo

    // Sort state — only accept columns we actually understand.
    const validSortColumns: ReadonlyArray<NonNullable<CustomerSortState['column']>> = [
      'name',
      'email',
      'created',
      'updated',
      'last_seen_at',
      'status',
    ]
    const sortColumnRaw = searchParams.get('sort')
    const sortColumn =
      sortColumnRaw &&
      (validSortColumns as readonly string[]).includes(sortColumnRaw)
        ? (sortColumnRaw as CustomerSortState['column'])
        : 'last_seen_at'
    const sortDirRaw = searchParams.get('dir')
    const sortDir: 'asc' | 'desc' =
      sortDirRaw === 'asc' || sortDirRaw === 'desc' ? sortDirRaw : 'desc'

    // Selected customer
    const selected = searchParams.get('selected')

    set({
      filters,
      sortState: { column: sortColumn, direction: sortDir },
      selectedId: selected,
    })
  },

  serializeToUrl: () => {
    const state = get()
    const params = new URLSearchParams()

    // Filters
    if (state.filters.q) params.set('q', state.filters.q)
    if (state.filters.status?.length)
      params.set('status', state.filters.status.join(','))
    if (state.filters.domain?.length)
      params.set('domain', state.filters.domain.join(','))
    if (state.filters.tags?.length)
      params.set('tags', state.filters.tags.join(','))
    if (state.filters.email_verified !== undefined)
      params.set('email_verified', state.filters.email_verified ? 'true' : 'false')
    if (state.filters.joined_from)
      params.set('joined_from', state.filters.joined_from)
    if (state.filters.joined_to)
      params.set('joined_to', state.filters.joined_to)
    if (state.filters.active_from)
      params.set('active_from', state.filters.active_from)
    if (state.filters.active_to)
      params.set('active_to', state.filters.active_to)

    // Sort
    if (state.sortState.column) params.set('sort', state.sortState.column)
    if (state.sortState.direction) params.set('dir', state.sortState.direction)

    // Selection
    if (state.selectedId) params.set('selected', state.selectedId)

    return params
  },
}))

// ── Selectors ───────────────────────────────────────────────────────────────

/**
 * Select current customer detail from cache.
 */
export const selectCurrentDetail = (
  state: CustomersStore
): CustomerDetail | null => {
  if (!state.selectedId) return null
  return state.detailCache.get(state.selectedId) ?? null
}

/**
 * Select unique domains from the unfiltered base dataset, so the domain
 * filter facet keeps showing every domain even when other filters are
 * applied.
 */
export const selectDomains = (state: CustomersStore): string[] => {
  const domains = new Set<string>()
  state.customersAll.forEach((c) => {
    const domain = extractDomain(c.email)
    if (domain) domains.add(domain)
  })
  return Array.from(domains).sort()
}

/**
 * Check if any filters are active.
 */
export const selectHasActiveFilters = (state: CustomersStore): boolean => {
  const f = state.filters
  return !!(
    f.q ||
    f.status?.length ||
    f.domain?.length ||
    f.tags?.length ||
    f.email_verified !== undefined ||
    f.joined_from ||
    f.joined_to ||
    f.active_from ||
    f.active_to
  )
}
